<!-- Pricing Modal -->
<app-pricing-modal
  [isOpen]="isPricingModalOpen()"
  [target]="paywallTarget()"
  (closed)="closePricingModal()"
  (productSelected)="onPricingProductSelected()"
  (paymentSuccess)="onPaymentSuccess($event)"
></app-pricing-modal>

<!-- Subscription Success Modal -->
<app-subscription-success-modal
  [isOpen]="isSubscriptionSuccessOpen()"
  [productName]="subscribedProductName()"
  (closed)="closeSubscriptionSuccess()"
></app-subscription-success-modal>

@if (isOpen$ | async) {
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-sm animate-fade-in"
    (click)="close()"
  >
    <div
      class="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden max-h-[90vh] flex flex-col"
      (click)="$event.stopPropagation()"
    >
      <!-- Header -->
      <div class="p-4 sm:p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
        <h3 class="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2">
          <lucide-icon [img]="icons.Share2" class="w-5 h-5 text-brand"></lucide-icon>
          Export Lesson
        </h3>
        <button (click)="close()" class="text-gray-400 hover:text-gray-600 cursor-pointer p-1">
          <lucide-icon [img]="icons.X" class="w-5 h-5"></lucide-icon>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 sm:p-5 space-y-3 overflow-y-auto">
        <p class="text-sm text-gray-500 mb-3">
          Choose a format to save your lesson plan and slides.
        </p>

        <!-- Google Slides Option (Primary) -->
        <button
          (click)="handleGoogleSlides()"
          class="w-full flex items-center gap-3 p-3 sm:p-4 border-2 border-brand rounded-xl bg-brand/5 hover:bg-brand/10 transition-all group text-left cursor-pointer"
        >
          <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand/20 text-brand flex items-center justify-center shrink-0">
            <lucide-icon [img]="icons.Presentation" class="w-5 h-5 sm:w-6 sm:h-6"></lucide-icon>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-bold text-brand-dark">Google Slides</span>
              <span class="bg-brand text-white text-[10px] px-2 py-0.5 rounded-full">Recommended</span>
            </div>
            <span class="text-xs text-gray-500 block mt-0.5">Open your presentation in Google Slides.</span>
          </div>
        </button>

        <!-- PDF Option -->
        <button
          (click)="handlePdfExport()"
          [disabled]="(isExportingPdf$ | async)"
          class="w-full flex items-center gap-3 p-3 sm:p-4 border border-gray-200 rounded-xl hover:border-brand hover:bg-brand/5 transition-all group text-left cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 group-hover:bg-brand/10 group-hover:text-brand transition-colors">
            @if (isExportingPdf$ | async) {
              <lucide-icon [img]="icons.Loader2" class="w-5 h-5 sm:w-6 sm:h-6 animate-spin"></lucide-icon>
            } @else {
              <lucide-icon [img]="icons.Printer" class="w-5 h-5 sm:w-6 sm:h-6"></lucide-icon>
            }
          </div>
          <div class="flex-1 min-w-0">
            <span class="font-bold text-gray-800 group-hover:text-brand-dark block">
              @if (isExportingPdf$ | async) {
                Generating PDF...
              } @else {
                Teacher Guide (PDF)
              }
            </span>
            <span class="text-xs text-gray-500 block mt-0.5">Optimized for printing. Includes Plan + Script.</span>
          </div>
        </button>

        <!-- PPTX Option -->
        <button
          (click)="handlePPTX()"
          [disabled]="(isExportingPptx$ | async)"
          class="w-full flex items-center gap-3 p-3 sm:p-4 border border-gray-200 rounded-xl hover:border-brand hover:bg-brand/5 transition-all group text-left cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0 group-hover:bg-brand/10 group-hover:text-brand transition-colors">
            @if (isExportingPptx$ | async) {
              <lucide-icon [img]="icons.Loader2" class="w-5 h-5 sm:w-6 sm:h-6 animate-spin"></lucide-icon>
            } @else {
              <lucide-icon [img]="icons.Presentation" class="w-5 h-5 sm:w-6 sm:h-6"></lucide-icon>
            }
          </div>
          <div class="flex-1 min-w-0">
            <span class="font-bold text-gray-800 group-hover:text-brand-dark block">
              @if (isExportingPptx$ | async) {
                Generating PPTX...
              } @else {
                Slide Deck (.pptx)
              }
            </span>
            <span class="text-xs text-gray-500 block mt-0.5">PowerPoint compatible file with speaker notes.</span>
          </div>
        </button>

        <!-- Admin Plan Google Docs Option -->
        <button
          (click)="handleGoogleDocs()"
          [disabled]="(isExportingGoogleDocs$ | async)"
          class="w-full flex items-center gap-3 p-3 sm:p-4 border border-gray-200 rounded-xl hover:border-brand hover:bg-brand/5 transition-all group text-left cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0 group-hover:bg-brand/10 group-hover:text-brand transition-colors">
            @if (isExportingGoogleDocs$ | async) {
              <lucide-icon [img]="icons.Loader2" class="w-5 h-5 sm:w-6 sm:h-6 animate-spin"></lucide-icon>
            } @else {
              <lucide-icon [img]="icons.FileSpreadsheet" class="w-5 h-5 sm:w-6 sm:h-6"></lucide-icon>
            }
          </div>
          <div class="flex-1 min-w-0">
            <span class="font-bold text-gray-800 group-hover:text-brand-dark block">
              @if (isExportingGoogleDocs$ | async) {
                Creating Google Doc...
              } @else {
                Admin Plan (Google Docs)
              }
            </span>
            <span class="text-xs text-gray-500 block mt-0.5">Open administrative plan in Google Docs.</span>
          </div>
        </button>
      </div>

      <!-- Safe area padding for mobile -->
      <div class="h-safe-area-inset-bottom sm:hidden"></div>
    </div>
  </div>
}
